import type { SVGProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * The feature's animated success mark: the circle draws itself in, the tick
 * strokes in behind it, and the whole mark pops with a spring — then rests
 * static. Introduced for the sync-status chip (DV-2750; restored from the
 * pre-redesign callout per Steffon, QA round 5 follow-up).
 *
 * **Plays on mount and never again** — it is a CSS `animation: … forwards`,
 * not a transition. A caller that wants it to replay must mount it only while
 * the success state is showing (SyncStatusChip renders it solely in the synced
 * state; AssetImageDetails mounts it for the life of a copy confirmation).
 * Keeping it permanently mounted renders an already-drawn, static mark.
 *
 * Decorative only — the caller's text carries the meaning. Colored via
 * currentColor; pair with `text-success`.
 *
 * Two consumers today (`SyncStatusChip`, `AssetImageDetails`), so it is no
 * longer edit-suite-specific despite living under `edit/`; move it if a third
 * arrives.
 */
const SyncedCheckmark = ({
  className,
  ...props
}: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 100 100"
    aria-hidden="true"
    className={cn('po-sync-check-pop h-5 w-5 shrink-0', className)}
    {...props}
  >
    <circle
      className="po-sync-check-circle"
      cx="50"
      cy="50"
      r="40"
      fill="none"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
    />
    <polyline
      className="po-sync-check-tick"
      points="35 50 45 60 65 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default SyncedCheckmark;
